import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './index.module.less';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { InstagramQueryPostItemModel } from '@/types/anonymously.interface';
import { formatNumberToK } from '@/shared/utils';
import { useUserProvider } from '@/context/UserProvider';
import { fetchInsFamousQueryPostsData } from '@/lib/service/anonymously';
import { Button, message, Spin } from 'antd';
import { DownloadIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getInsStoryDownload } from '@/lib/service/dashboard';
import dayjs from 'dayjs';
import { LimitAlert } from '@/components/LandingPage/FeeLimitAlert';

// CLS 优化：关键组件使用同步导入，避免异步加载导致的布局偏移
import LocalImage from '@/components/Common/LocalImage';
import ImageGallery from '@/components/Common/ImageGallery';
import PostsAnalysis from '../PostsAnalysis';

const PostResultContent = ({
  showDownload = false,
  hideTitle = false,
  locked = false,
  loadMoreNotCheckLogin,
  post_page_token,
  dataList,
  gotoPlanClick,
  analyzeAll,
}: {
  showDownload?: boolean;
  hideTitle?: boolean;
  locked?: boolean;
  loadMoreNotCheckLogin?: boolean;
  post_page_token?: string;
  dataList?: InstagramQueryPostItemModel[];
  gotoPlanClick?: () => void;
  analyzeAll?: boolean;
}) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [show, setShow] = useState(false);
  const [pageToken, setPageToken] = useState(post_page_token);
  const [index, setIndex] = useState(1);
  const [postList, setPostList] = useState<InstagramQueryPostItemModel[]>([]);
  const { isLogin, onOpenLoginModal, onChangedLoginView } = useUserProvider();
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [showLimit, setShowLimit] = useState(false);

  const showMore = useMemo(() => {
    if (locked) {
      return false;
    }
    if (showDownload) {
      return index * 12 < dataList?.length;
    } else {
      return !!pageToken;
    }
  }, [index, dataList, showDownload, pageToken]);

  const showPostList = useMemo(() => {
    if (showDownload) {
      if (index * 12 < dataList?.length) {
        return dataList?.slice(0, index * 12);
      } else {
        return dataList;
      }
    } else {
      return postList;
    }
  }, [index, dataList, showDownload, postList]);

  useEffect(() => {
    setCurrentIndex(0);
    setShow(false);
    setPostList(dataList);
    setPageToken(post_page_token);
    setIndex(1);
  }, [dataList, post_page_token]);

  const onSearch = () => {
    if (!loadMoreNotCheckLogin && !isLogin) {
      onOpenLoginModal(true);
      onChangedLoginView(false);
      return;
    }

    if (showDownload) {
      setIndex(() => index + 1);
      return;
    }

    const media_name = postList?.[0]?.media_name;

    if (!media_name || !pageToken) {
      return;
    }

    if (loading) {
      return;
    }
    setLoading(true);

    fetchInsFamousQueryPostsData(media_name, pageToken)
      .then((res) => {
        if (res?.code === 0) {
          const TempItems = res?.data?.items?.map((item) => ({
            ...item,
            media_list: item.carousel_media,
            caption: typeof item.caption === 'string' ? item.caption : item.caption?.text,
            cover_image: item.thumbnail_url,
            is_video:
              item.carousel_media?.length > 0
                ? item.carousel_media.find((media) => media.media_type === 2)
                  ? true
                  : item.is_video
                : false,
          }));
          setPostList([...postList, ...(TempItems as InstagramQueryPostItemModel[])]);
          setPageToken(res?.data?.page_token);
          return;
        } else {
          setPageToken(null);
          message.error(res?.message);
        }
      })
      .catch((error) => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const isSingleMedia = useCallback((item: InstagramQueryPostItemModel) => {
    return item?.media_list?.length === 1;
  }, []);

  const onDownloadClick = (postItemModel: InstagramQueryPostItemModel) => {
    if (!isLogin) {
      onChangedLoginView(false);
      onOpenLoginModal(true);
      return;
    }
    if (downloadLoading) {
      return;
    }
    setDownloadLoading(false);

    getInsStoryDownload(postItemModel?.media_name, 'single', null)
      .then((res) => {
        if (res?.code === 0) {
          handleDownload(postItemModel);
          return;
        }
        if (res?.code === 22010 || res?.code === 22011) {
          setShowLimit(true);
          return;
        } else {
          message.error(res?.message);
        }
      })
      .finally(() => {
        setDownloadLoading(false);
      });
  };

  const handleDownload = async (postItemModel: InstagramQueryPostItemModel) => {
    if (!postItemModel) {
      return;
    }

    let imgUrlStr;
    const currentMedia = postItemModel?.media_list[0];
    if (postItemModel?.media_list?.length > 0) {
      imgUrlStr = currentMedia?.media_url;
    } else {
      imgUrlStr = postItemModel?.cover_image;
    }

    if (!imgUrlStr) return;

    try {
      const response = await fetch(`/n-api/download-image?url=${encodeURIComponent(imgUrlStr)}`);
      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      if (currentMedia?.media_type === 2) {
        a.download = `Dolphin_Radar_${dayjs().format('YYYYMMDDHHmmss')}.mp4`;
      } else {
        a.download = `Dolphin_Radar_${dayjs().format('YYYYMMDDHHmmss')}.png`;
      }
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  return (
    <>
      {dataList?.length > 0 && (
        <PostsAnalysis post_list={dataList} analyzeAll={analyzeAll} locked={locked} gotoPlanClick={gotoPlanClick} />
      )}
      <div className={styles.content}>
        {!hideTitle && (
          <div className={styles.sectionTitle}>
            <span className={styles.count}>{postList?.length ?? 0}</span> {t('New Posts')}
          </div>
        )}

        {dataList?.length > 0 ? (
          <>
            <div className={styles.listContainer}>
              {showPostList?.map((item: InstagramQueryPostItemModel, index: number) => {
                return (
                  <div
                    className={cn(styles.item, 'group')}
                    key={index}
                    onClick={() => {
                      setCurrentIndex(index);
                      setShow(true);
                    }}
                  >
                    <LocalImage
                      alt=""
                      // 搜索时返回的图片字段是 cover_image，而分页加载时返回的图片字段是 thumbnail_url
                      src={item?.cover_image || item?.thumbnail_url}
                      style={{ width: '100%', height: '100%' }}
                    />
                    {item?.is_video && isSingleMedia(item) && (
                      <Image
                        alt=""
                        src={'/images/toolkit/video-small-icon.webp'}
                        width={20}
                        height={20}
                        loading="lazy"
                        style={{ position: 'absolute', top: '15px', right: '15px' }}
                      />
                    )}
                    {!isSingleMedia(item) && (
                      <Image
                        alt=""
                        src={'/images/toolkit/img-small-icon.webp'}
                        width={20}
                        height={20}
                        loading="lazy"
                        style={{ position: 'absolute', top: '15px', right: '15px' }}
                      />
                    )}
                    <div className={styles.infoContainer}>
                      <div className={styles.infoDataContainer}>
                        <Image alt="" src={'/images/toolkit/comment-icon.webp'} width={20} height={20} loading="lazy" />
                        <span style={{ marginRight: '10px' }}>{formatNumberToK(item?.comment_count)}</span>
                        <Image alt="" src={'/images/toolkit/like-icon.webp'} width={20} height={20} loading="lazy" />
                        <span>{formatNumberToK(item?.like_count)}</span>
                      </div>
                      {item?.publish_time && <div>{item?.publish_time}</div>}
                      <Button
                        icon={<DownloadIcon className="!flex mx-auto" size={20} />}
                        className={cn('absolute bottom-4 right-4 flex group-hover:block hidden', {
                          '!hidden': !isSingleMedia(item),
                        })}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDownloadClick(item);
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CLS 优化：More 按钮 loading 时显示骨架屏预占位，避免新数据加载后布局偏移 */}
            {loading && (
              <div className={styles.listContainer} style={{ marginTop: '24px' }}>
                {Array.from({ length: 12 }, (_, i) => (
                  <div key={`skeleton-${i}`} className={styles.item}>
                    <div className={styles.skeletonInner} />
                  </div>
                ))}
              </div>
            )}

            {showMore && (
              <div className={styles.moreButton} onClick={onSearch}>
                {loading ? <Spin size="small" /> : t('More')}
              </div>
            )}

            {showMore && !showDownload && <div className={styles.line} />}
          </>
        ) : (
          <>
            {!(dataList?.length > 0) && (
              <div className={styles.noData}>
                <span>{t('The account you entered has no posts.')}</span>
              </div>
            )}
          </>
        )}
      </div>

      {show && (
        <ImageGallery
          dataList={postList}
          index={currentIndex}
          show={show}
          onClose={() => setShow(false)}
          gotoPlanClick={gotoPlanClick}
        />
      )}

      <LimitAlert
        open={showLimit}
        closeAction={(isOk) => {
          setShowLimit(false);
          if (isOk) {
            setTimeout(() => {
              gotoPlanClick && gotoPlanClick();
              const dom = document.getElementById('1');
              dom && dom.scrollIntoView({ behavior: 'smooth' });
            }, 20);
          }
        }}
      />
    </>
  );
};

export default PostResultContent;
